import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, vModelText as _vModelText, withDirectives as _withDirectives, createTextVNode as _createTextVNode, normalizeClass as _normalizeClass, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, toDisplayString as _toDisplayString, normalizeStyle as _normalizeStyle, createStaticVNode as _createStaticVNode } from "vue"

const _hoisted_1 = { class: "form-section" }
const _hoisted_2 = { class: "date-row" }
const _hoisted_3 = { class: "date-field" }
const _hoisted_4 = ["max"]
const _hoisted_5 = { class: "date-field" }
const _hoisted_6 = ["min", "max"]
const _hoisted_7 = { class: "form-section" }
const _hoisted_8 = { class: "time-slots" }
const _hoisted_9 = ["disabled"]
const _hoisted_10 = {
  key: 0,
  class: "spinner"
}
const _hoisted_11 = { key: 1 }
const _hoisted_12 = {
  key: 1,
  class: "legend"
}

import { ref, computed, watch, onMounted } from "vue";
import {
	submitDensityJob,
	waitForJob,
	downloadResults,
	type TrafficDensityParams,
	type JobStatus,
} from "@/services/trafficDensity";

type TimeSlot = "morning" | "afternoon" | "evening";


export default /*@__PURE__*/_defineComponent({
  __name: 'TrafficDensityPanel',
  props: {
    visible: { type: Boolean },
    selectedCity: {},
    bbox: {},
    map: {}
  },
  emits: ["close", "results"],
  setup(__props: any, { emit: __emit }) {

const props = __props;

const emit = __emit;

// Form State
const dateFrom = ref("");
const dateTo = ref("");
const timeSets = ref<TimeSlot[]>(["morning", "afternoon", "evening"]);
const loading = ref(false);
const statusMessage = ref("");
const statusType = ref<"info" | "success" | "error">("info");
const showLegend = ref(false);

// Drag State
const position = ref({ x: 0, y: 75 });
const isDragging = ref(false);
const dragOffset = ref({ x: 0, y: 0 });

function resetPosition() {
	if (!props.map) return;
	const container = props.map.getContainer();
	const width = container.clientWidth;
	const height = container.clientHeight;

	if (width < 768) {
		// Mobile: bottom-right
		position.value = { x: width - 340, y: height - 420 };
	} else {
		// Desktop: Top-left (offset to be below icons)
		position.value = { x: 20, y: 75 };
	}
}

onMounted(() => {
	resetPosition();
});

// Computed
// Max date is yesterday, as today is still in progress
const maxAllowedDate = computed(() => {
	const d = new Date();
	d.setDate(d.getDate() - 1);
	return d.toISOString().split("T")[0];
});

const canAnalyze = computed(() => {
	return (
		props.bbox &&
		props.selectedCity &&
		dateFrom.value &&
		dateTo.value &&
		timeSets.value.length > 0
	);
});

// Methods
function toggleTimeSet(slot: TimeSlot) {
	const idx = timeSets.value.indexOf(slot);
	if (idx >= 0) {
		timeSets.value.splice(idx, 1);
	} else {
		timeSets.value.push(slot);
	}
}

// Quick date setters
function setLastWeek() {
	const now = new Date();
	const end = new Date(now);
	end.setDate(now.getDate() - 1); // Yesterday

	const start = new Date(end);
	start.setDate(end.getDate() - 7);

	dateFrom.value = start.toISOString().split("T")[0];
	dateTo.value = end.toISOString().split("T")[0];
}

function setLastMonth() {
	const now = new Date();
	const end = new Date(now);
	end.setDate(now.getDate() - 1); // Yesterday

	const start = new Date(end);
	start.setMonth(end.getMonth() - 1);

	dateFrom.value = start.toISOString().split("T")[0];
	dateTo.value = end.toISOString().split("T")[0];
}

function setLastYear() {
	const now = new Date();
	const end = new Date(now);
	end.setDate(now.getDate() - 1); // Yesterday

	const start = new Date(end);
	start.setFullYear(end.getFullYear() - 1);

	dateFrom.value = start.toISOString().split("T")[0];
	dateTo.value = end.toISOString().split("T")[0];
}

// Drag handlers
function startDrag(e: MouseEvent) {
	if ((e.target as HTMLElement).classList.contains("close-btn")) return;

	isDragging.value = true;
	dragOffset.value = {
		x: e.clientX - position.value.x,
		y: e.clientY - position.value.y,
	};

	document.addEventListener("mousemove", onDrag);
	document.addEventListener("mouseup", stopDrag);
}

function onDrag(e: MouseEvent) {
	if (!isDragging.value) return;

	position.value = {
		x: e.clientX - dragOffset.value.x,
		y: e.clientY - dragOffset.value.y,
	};
}

function stopDrag() {
	isDragging.value = false;
	document.removeEventListener("mousemove", onDrag);
	document.removeEventListener("mouseup", stopDrag);
}

async function startAnalysis() {
	if (!canAnalyze.value || !props.bbox) return;

	loading.value = true;
	statusMessage.value = "Invio richiesta a TomTom...";
	statusType.value = "info";

	try {
		const params: TrafficDensityParams = {
			jobName: `Densità ${props.selectedCity} ${dateFrom.value}`,
			bbox: props.bbox,
			dateFrom: dateFrom.value,
			dateTo: dateTo.value,
			timeSets: [...timeSets.value],
		};

		// Submit job
		const jobId = await submitDensityJob(params);
		statusMessage.value = "Elaborazione in corso...";

		// Poll for completion
		const resultUrlOrData = await waitForJob(
			jobId,
			(status: JobStatus) => {
				statusMessage.value = `Stato: ${status.state}`;
			},
			5000,
			60,
			props.bbox,
			params.timeSets,
		);

		// Handle demo mode (returns GeoJSON directly) vs real API (returns URL)
		let results;
		if (typeof resultUrlOrData === "string") {
			// Real API: download from URL
			statusMessage.value = "Scaricamento risultati...";
			results = await downloadResults(resultUrlOrData);
		} else {
			// Demo mode: data already returned
			results = resultUrlOrData;
		}

		statusMessage.value = "Analisi completata!";
		statusType.value = "success";
		showLegend.value = true;

		emit("results", results);
	} catch (err: any) {
		console.error("[TrafficDensityPanel] Error:", err);
		statusMessage.value = `Errore: ${err.message}`;
		statusType.value = "error";
	} finally {
		loading.value = false;
	}
}

// Reset status when panel closes/opens
watch(
	() => props.visible,
	(v) => {
		if (v) {
			resetPosition();
			statusMessage.value = "";
			showLegend.value = false;
		}
	},
);

return (_ctx: any,_cache: any) => {
  return (__props.visible)
    ? (_openBlock(), _createElementBlock("div", {
        key: 0,
        class: "traffic-density-panel",
        style: _normalizeStyle({ top: position.value.y + 'px', left: position.value.x + 'px' })
      }, [
        _createElementVNode("div", {
          class: "panel-header",
          onMousedown: startDrag
        }, [
          _cache[6] || (_cache[6] = _createElementVNode("h3", null, "📊 Analisi Densità Traffico", -1)),
          _createElementVNode("button", {
            class: "close-btn",
            onClick: _cache[0] || (_cache[0] = ($event: any) => (_ctx.$emit('close')))
          }, "✕")
        ], 32),
        _createElementVNode("div", _hoisted_1, [
          _cache[9] || (_cache[9] = _createElementVNode("label", null, "Periodo", -1)),
          _createElementVNode("div", { class: "quick-dates" }, [
            _createElementVNode("button", {
              class: "quick-btn",
              onClick: setLastWeek
            }, "📅 Ultima Sett."),
            _createElementVNode("button", {
              class: "quick-btn",
              onClick: setLastMonth
            }, "📆 Ultimo Mese"),
            _createElementVNode("button", {
              class: "quick-btn",
              onClick: setLastYear
            }, "🗓️ Ultimo Anno")
          ]),
          _createElementVNode("div", _hoisted_2, [
            _createElementVNode("div", _hoisted_3, [
              _cache[7] || (_cache[7] = _createElementVNode("span", { class: "field-label" }, "Da", -1)),
              _withDirectives(_createElementVNode("input", {
                type: "date",
                "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((dateFrom).value = $event)),
                max: dateTo.value || maxAllowedDate.value
              }, null, 8, _hoisted_4), [
                [_vModelText, dateFrom.value]
              ])
            ]),
            _createElementVNode("div", _hoisted_5, [
              _cache[8] || (_cache[8] = _createElementVNode("span", { class: "field-label" }, "A", -1)),
              _withDirectives(_createElementVNode("input", {
                type: "date",
                "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((dateTo).value = $event)),
                min: dateFrom.value,
                max: maxAllowedDate.value
              }, null, 8, _hoisted_6), [
                [_vModelText, dateTo.value]
              ])
            ])
          ])
        ]),
        _createElementVNode("div", _hoisted_7, [
          _cache[13] || (_cache[13] = _createElementVNode("label", null, "Fasce Orarie", -1)),
          _createElementVNode("div", _hoisted_8, [
            _createElementVNode("button", {
              class: _normalizeClass(["slot-btn", { active: timeSets.value.includes('morning') }]),
              onClick: _cache[3] || (_cache[3] = ($event: any) => (toggleTimeSet('morning')))
            }, [...(_cache[10] || (_cache[10] = [
              _createTextVNode(" 🌅 Mattina", -1),
              _createElementVNode("br", null, null, -1),
              _createElementVNode("small", null, "06:00-14:00", -1)
            ]))], 2),
            _createElementVNode("button", {
              class: _normalizeClass(["slot-btn", { active: timeSets.value.includes('afternoon') }]),
              onClick: _cache[4] || (_cache[4] = ($event: any) => (toggleTimeSet('afternoon')))
            }, [...(_cache[11] || (_cache[11] = [
              _createTextVNode(" ☀️ Pomeriggio", -1),
              _createElementVNode("br", null, null, -1),
              _createElementVNode("small", null, "14:00-22:00", -1)
            ]))], 2),
            _createElementVNode("button", {
              class: _normalizeClass(["slot-btn", { active: timeSets.value.includes('evening') }]),
              onClick: _cache[5] || (_cache[5] = ($event: any) => (toggleTimeSet('evening')))
            }, [...(_cache[12] || (_cache[12] = [
              _createTextVNode(" 🌙 Sera", -1),
              _createElementVNode("br", null, null, -1),
              _createElementVNode("small", null, "22:00-06:00", -1)
            ]))], 2)
          ])
        ]),
        _createElementVNode("button", {
          class: "analyze-btn",
          disabled: !canAnalyze.value || loading.value,
          onClick: startAnalysis
        }, [
          (loading.value)
            ? (_openBlock(), _createElementBlock("span", _hoisted_10))
            : (_openBlock(), _createElementBlock("span", _hoisted_11, "🚀 Analizza Traffico"))
        ], 8, _hoisted_9),
        (statusMessage.value)
          ? (_openBlock(), _createElementBlock("div", {
              key: 0,
              class: _normalizeClass(["status-message", statusType.value])
            }, _toDisplayString(statusMessage.value), 3))
          : _createCommentVNode("", true),
        (showLegend.value)
          ? (_openBlock(), _createElementBlock("div", _hoisted_12, [...(_cache[14] || (_cache[14] = [
              _createStaticVNode("<label data-v-d93d73fe>Densità Traffico</label><div class=\"legend-scale\" data-v-d93d73fe><div class=\"legend-item\" style=\"background:#2ecc71;\" data-v-d93d73fe>Bassa</div><div class=\"legend-item\" style=\"background:#f1c40f;\" data-v-d93d73fe>Media</div><div class=\"legend-item\" style=\"background:#e67e22;\" data-v-d93d73fe>Alta</div><div class=\"legend-item\" style=\"background:#e74c3c;\" data-v-d93d73fe>Molto Alta</div></div>", 2)
            ]))]))
          : _createCommentVNode("", true)
      ], 4))
    : _createCommentVNode("", true)
}
}

})